import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import EventForm from '../components/EventForm';
import AccidentForm from '../components/AccidentForm';
import CostForm from '../components/CostForm';
import { COST_TYPE_COLORS as TYPE_COLORS } from '../utils/costTypes';

const DAMAGE_COLORS = { minor: '#f59e0b', major: '#ef4444', total_loss: '#7c3aed' };
const DAMAGE_LABELS = { minor: 'Minor', major: 'Major', total_loss: 'Total Loss' };

const getStatus = (event) => {
  const st = (event.status || '').toLowerCase();
  if (st.includes('delay') || st.includes('late') || st.includes('pending') || st.includes('intarziere')) return 'critical';
  if (st.includes('complete') || st.includes('done') || st.includes('finished') || st.includes('finalizat')) return 'good';
  return 'active';
};
const STATUS_LABEL = { good: 'COMPLETED', critical: 'DELAY', active: 'ACTIVE' };
const STATUS_COLOR = { good: '#10b981', critical: '#ef4444', active: '#3b82f6' };
const STATUS_BG    = { good: '#ecfdf5',  critical: '#fef2f2',  active: '#eff6ff' };

const formatDate = (value) => {
  if (!value) return '—';
  const s = String(value);
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const dt = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(s);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString();
};

const getAvatar = (name) => (name || 'EV').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

const DriverEvents = () => {
  const { user } = useAppContext();
  const [events, setEvents] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // detail-view related data
  const [relatedCosts, setRelatedCosts] = useState([]);
  const [relatedVehicle, setRelatedVehicle] = useState(null);
  const [showAccidentForm, setShowAccidentForm] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [evtRes, accRes] = await Promise.all([apiEndpoints.events.list(), apiEndpoints.accidents.list()]);
      const driverName = (user?.name || '').toLowerCase();
      setEvents((evtRes.data || []).filter(e => e.resp && e.resp.toLowerCase() === driverName));
      setAccidents(accRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Fetch related costs + vehicle whenever a row is selected
  useEffect(() => {
    if (!selected?.plate) { setRelatedCosts([]); setRelatedVehicle(null); return; }
    const plate = selected.plate;
    Promise.all([apiEndpoints.costs.list(), apiEndpoints.vehicles.list()])
      .then(([costsRes, vehiclesRes]) => {
        setRelatedCosts((costsRes.data || []).filter(c => c.plate === plate).sort((a, b) => new Date(b.date) - new Date(a.date)));
        setRelatedVehicle((vehiclesRes.data || []).find(v => v.plate === plate) || null);
      })
      .catch(() => {});
  }, [selected?.plate]);

  const getAccidentsForPlate = (plate) => accidents.filter(a => a.plate && plate && a.plate.trim() === plate.trim());

  const handleSaveEvent = async (payload) => {
    try { await apiEndpoints.events.create({ ...payload, resp: user.name }); setShowForm(false); await load(); }
    catch { alert('Error saving event. Please try again.'); }
  };

  const handleSaveAccident = async (data, file) => {
    try { await apiEndpoints.accidents.create({ ...data, driverName: user.name, driverId: user.driverId || '' }, file); setShowAccidentForm(false); }
    catch { alert('Error saving accident. Please try again.'); }
  };

  const handleSaveCost = async (data, file) => {
    try {
      await apiEndpoints.costs.create({ ...data, driverId: user.driverId || '' }, file);
      setShowCostForm(false);
      if (selected?.plate) {
        apiEndpoints.costs.list().then(res => setRelatedCosts((res.data || []).filter(c => c.plate === selected.plate)));
      }
    } catch { alert('Error saving cost. Please try again.'); }
  };

  if (loading) return <div className="page-loading">Loading events…</div>;

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selected) {
    const status = getStatus(selected);
    const statusColor = STATUS_COLOR[status];
    const statusBg    = STATUS_BG[status];
    const relatedAccidents = getAccidentsForPlate(selected.plate);
    const plate = selected.plate || '';

    return (
      <div>
        <button className="back-btn" onClick={() => setSelected(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to events
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{plate}</span></div>
          <div className="vprofile-model">{selected.evt}</div>
          <div className="vprofile-sub">{selected.car} • {selected.resp || 'Unassigned'}</div>
        </div>

        <div className="detail-grid">
          {/* Event Details */}
          <div className="card">
            <div className="card-header"><div className="card-title">Event Details</div></div>
            <div className="card-body">
              {[['Vehicle plate', plate], ['Car', selected.car || '—'], ['Event', selected.evt || '—'], ['Responsible', selected.resp || 'Unassigned'], ['Created', formatDate(selected.created)], ['Est. completion', formatDate(selected.estEnd)]].map(([lbl, val]) => (
                <div key={lbl} className="info-row"><div className="info-lbl">{lbl}</div><div className="info-val">{val}</div></div>
              ))}
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <div className="info-lbl">Status</div>
                <div className="info-val">
                  <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', color: statusColor, backgroundColor: statusBg }}>{STATUS_LABEL[status]}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card quick-actions-card">
            <div className="card-header"><div className="card-title">Quick Actions</div></div>
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowForm(true)}>Add Event</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowAccidentForm(true)}>Report Accident</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowCostForm(true)}>Add Cost</button>
            </div>
          </div>

          {/* Accidents for this vehicle */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">
                Accidents
                {relatedAccidents.length > 0 && <span style={{ marginLeft: '8px', background: '#fee2e2', color: '#ef4444', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, padding: '2px 8px' }}>{relatedAccidents.length}</span>}
              </div>
            </div>
            <div className="card-body">
              {relatedAccidents.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>No accidents recorded for <strong>{plate}</strong>.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '480px' }}>
                    <thead><tr style={{ borderBottom: '1px solid #e5e7eb' }}>{['DATE', 'DAMAGE', 'REPAIR COST', 'CLAIM', 'AT FAULT'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>)}</tr></thead>
                    <tbody>
                      {relatedAccidents.map(acc => (
                        <tr key={acc._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '10px 12px' }}>{formatDate(acc.date)}</td>
                          <td style={{ padding: '10px 12px' }}><span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: DAMAGE_COLORS[acc.damageLevel], backgroundColor: `${DAMAGE_COLORS[acc.damageLevel]}18` }}>{DAMAGE_LABELS[acc.damageLevel] || acc.damageLevel}</span></td>
                          <td style={{ padding: '10px 12px', fontWeight: 600 }}>{acc.repairCost ? `${acc.repairCost.toLocaleString()} ${acc.currency}` : '—'}</td>
                          <td style={{ padding: '10px 12px' }}><span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: acc.claimStatus === 'open' ? '#3b82f6' : acc.claimStatus === 'closed' ? '#10b981' : acc.claimStatus === 'rejected' ? '#ef4444' : '#9ca3af' }}>{acc.claimStatus || '—'}</span></td>
                          <td style={{ padding: '10px 12px', color: 'var(--text-3)' }}>{{ driver: 'Our Driver', third_party: 'Third Party', unknown: 'Unknown' }[acc.atFault] || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Accident Summary */}
          {relatedAccidents.length > 0 ? (
            <div className="card">
              <div className="card-header"><div className="card-title">Accident Summary</div></div>
              <div className="card-body" style={{ textAlign: 'center', padding: '20px', display: 'grid', gap: '12px' }}>
                <div><div style={{ fontSize: '36px', fontWeight: 800, color: '#ef4444' }}>{relatedAccidents.length}</div><div style={{ fontSize: '13px', color: 'var(--text-3)' }}>accident{relatedAccidents.length !== 1 ? 's' : ''} on record</div></div>
                <div><div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-1)' }}>{relatedAccidents.reduce((s, a) => s + (a.repairCost || 0), 0).toLocaleString()} RON</div><div style={{ fontSize: '12px', color: 'var(--text-3)' }}>total repair cost</div></div>
              </div>
            </div>
          ) : <div />}

          {/* Vehicle Info */}
          <div className="card">
            <div className="card-header"><div className="card-title">Vehicle</div></div>
            <div className="card-body">
              {relatedVehicle ? (
                <>
                  {[['Registration', relatedVehicle.plate], ['Brand / Model', `${relatedVehicle.brand} ${relatedVehicle.model}`], ['Year / Fuel', `${relatedVehicle.year} · ${relatedVehicle.fuel}`], ['Body type', relatedVehicle.type || '—'], ['Company', relatedVehicle.company || '—'], ['Status', relatedVehicle.status || '—']].map(([lbl, val]) => (
                    <div key={lbl} className="info-row"><div className="info-lbl">{lbl}</div><div className="info-val">{val}</div></div>
                  ))}
                </>
              ) : <div style={{ color: 'var(--text-3)', padding: '12px 0', fontSize: '13px' }}>Vehicle info not available.</div>}
            </div>
          </div>

          {/* Costs for this vehicle */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Vehicle Costs</div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowCostForm(true)}>+ Add</button>
            </div>
            <div className="card-body">
              {relatedCosts.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>No costs recorded for this vehicle.</div>
              ) : (
                <>
                  {relatedCosts.slice(0, 6).map((c, i) => {
                    const color = TYPE_COLORS[c.costType] || '#6b7280';
                    return (
                      <div key={c._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < Math.min(relatedCosts.length, 6) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)' }}>{c.costType || 'Cost'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', paddingLeft: '14px' }}>{c.date ? new Date(c.date).toLocaleDateString() : '—'}{c.description ? ` • ${c.description.slice(0, 28)}` : ''}</div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: '#0077b6' }}>{Number(c.amount || 0).toLocaleString()}</span>
                          <span style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '3px' }}>{c.currency || 'RON'}</span>
                        </div>
                      </div>
                    );
                  })}
                  {relatedCosts.length > 6 && <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{relatedCosts.length - 6} more</div>}
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)' }}>{relatedCosts.length} entr{relatedCosts.length === 1 ? 'y' : 'ies'}</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-1)' }}>{relatedCosts.reduce((s, c) => s + (c.amount || 0), 0).toLocaleString()} <span style={{ fontSize: '11px', fontWeight: 500 }}>RON</span></span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <EventForm isOpen={showForm} onClose={() => setShowForm(false)} event={{ plate, car: selected.car || '' }} onSave={handleSaveEvent} />
        <AccidentForm isOpen={showAccidentForm} onClose={() => setShowAccidentForm(false)} defaultPlate={plate} defaultVehicleName={selected.car || ''} onSave={handleSaveAccident} />
        <CostForm isOpen={showCostForm} onClose={() => setShowCostForm(false)} cost={{ plate, vehicleName: selected.car || '' }} onSave={handleSaveCost} />
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">My Events</div>
          <div className="ph-sub">{events.length} event{events.length !== 1 ? 's' : ''} assigned to you</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
          New Event
        </button>
      </div>

      <div className="card">
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          <table className="events-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                {['VEHICLE', 'EVENT', 'RESPONSIBLE', 'CREATION DATE', 'EST. COMPLETION DATE', 'STATUS', 'ACTION'].map(h => (
                  <th key={h} style={{ textAlign: h === 'ACTION' ? 'right' : 'left', padding: '12px', color: '#9ca3af', fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No events assigned to you.</td></tr>
              ) : events.map(event => {
                const status = getStatus(event);
                const accidentCount = getAccidentsForPlate(event.plate).length;
                return (
                  <tr key={event._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => setSelected(event)}>
                    <td style={{ padding: '12px' }}><span style={{ display: 'inline-block', padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '13px', fontWeight: '500' }}>{event.plate}</span></td>
                    <td style={{ padding: '12px', fontSize: '14px' }}><div>{event.car}</div><div style={{ color: '#6b7280', fontSize: '13px' }}>{event.evt}</div></td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#059669', color: 'white', fontSize: '12px', fontWeight: '600' }}>{getAvatar(event.resp)}</span>
                        <span>{event.resp || 'Unassigned'}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>{formatDate(event.created)}</td>
                    <td style={{ padding: '12px', fontSize: '14px' }}>{formatDate(event.estEnd)}</td>
                    <td style={{ padding: '12px' }}><span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '9999px', fontSize: '12px', fontWeight: '600', color: STATUS_COLOR[status], backgroundColor: STATUS_BG[status] }}>{STATUS_LABEL[status]}</span></td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      {accidentCount > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: '#ef4444', backgroundColor: '#fee2e2', marginRight: '6px' }}>{accidentCount}</span>}
                      <button className="btn btn-ghost btn-xs" style={{ color: '#3b82f6' }} onClick={e => { e.stopPropagation(); setSelected(event); }}>Details</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <EventForm isOpen={showForm} onClose={() => setShowForm(false)} event={null} onSave={handleSaveEvent} />
    </div>
  );
};

export default DriverEvents;
